import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "./StudentProgress.css";
import "./StudentAttendance.css";
import NavbarAll from "../Navbar/NavbarAll";

function getInitial(nameOrEmail) {
  const s = (nameOrEmail || "?").trim();
  return s.charAt(0).toUpperCase();
}

export default function StudentAttendance({ user: hifzUser }) {
  const navigate = useNavigate();
  const location = useLocation();

  const apiURL = process.env.REACT_APP_API_URL;
  const params = new URLSearchParams(location.search);
  const deepLinkToken = params.get("token");

  const [student, setStudent] = useState(location.state?.student || null);
  const [studentId, setStudentId] = useState(
    () =>
      location.state?.student?.uid ||
      location.state?.student?.id ||
      hifzUser?.userId ||
      localStorage.getItem("userId")
  );

  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolvingStudent, setResolvingStudent] = useState(false);

  const monthNames = useMemo(
    () => [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ],
    []
  );

  const currentMonthName = monthNames[currentMonth];
  const studentDisplayName = student?.name || "Student";

  const goToDashboard = () => {
    navigate(
      { pathname: "/dashboard-student", search: location.search },
      { state: { ...location.state, student, role: "student" } }
    );
  };

  const goToProfile = () => {
    navigate(
      {
        pathname: "/dashboard-student/profile",
        search: location.search,
      },
      {
        state: student
          ? { student, from: "attendance" }
          : { from: "attendance" },
      }
    );
  };

  useEffect(() => {
    if (!deepLinkToken) return;
    setResolvingStudent(true);
    axios
      .get(`${apiURL}/students/by-token/${deepLinkToken}`)
      .then((res) => {
        setStudent(res.data);
        setStudentId(res.data?.uid);
      })
      .catch((err) => console.error("Failed to resolve deep link", err))
      .finally(() => setResolvingStudent(false));
  }, [deepLinkToken, apiURL]);

  useEffect(() => {
    if (deepLinkToken || !studentId || student) return;
    setResolvingStudent(true);
    const token = localStorage.getItem("token");
    axios
      .get(`${apiURL}/users/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res.data;
        setStudent({ ...data, id: data.uid, uid: data.uid });
      })
      .catch((err) => console.error("Failed to fetch user", err))
      .finally(() => setResolvingStudent(false));
  }, [studentId, deepLinkToken, student, apiURL]);

  useEffect(() => {
    if (studentId) return;
    const id = hifzUser?.userId || localStorage.getItem("userId");
    if (id) setStudentId(id);
  }, [studentId, hifzUser?.userId]);

  const fetchAttendanceSummary = async (month, year) => {
    if (!studentId && !deepLinkToken) return;
    setLoading(true);
    try {
      let res;
      if (deepLinkToken) {
        res = await axios.get(`${apiURL}/students/by-token/${deepLinkToken}/present-days`, {
          params: { month: month + 1, year },
        });
      } else {
        const token = localStorage.getItem("token");
        res = await axios.get(`${apiURL}/users/${studentId}/present-days`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { month: month + 1, year },
        });
      }
      setAttendanceSummary(res.data);
    } catch (err) {
      console.error("Error fetching attendance summary:", err);
      setAttendanceSummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceSummary(currentMonth, currentYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, deepLinkToken, currentMonth, currentYear, apiURL]);

  const handlePrevMonth = () => {
    const now = new Date();
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;

    const limitMonth = now.getMonth() - 1;
    const limitYear = now.getFullYear();

    if (prevYear < limitYear || (prevYear === limitYear && prevMonth < limitMonth)) {
      alert("You can only view last month's data.");
      return;
    }
    setCurrentMonth(prevMonth);
    setCurrentYear(prevYear);
  };

  const handleNextMonth = () => {
    const now = new Date();
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;

    if (
      nextYear > now.getFullYear() ||
      (nextYear === now.getFullYear() && nextMonth > now.getMonth())
    ) {
      alert("You cannot view future data.");
      return;
    }

    setCurrentMonth(nextMonth);
    setCurrentYear(nextYear);
  };

  const now = new Date();
  const oneMonthBefore = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const viewingDate = new Date(currentYear, currentMonth, 1);
  const showPrevButton = viewingDate > oneMonthBefore;
  const showNextButton =
    viewingDate < new Date(now.getFullYear(), now.getMonth(), 1);

  if (resolvingStudent) {
    return (
      <>
        <NavbarAll />
        <div className="hifz-progress-wrapper">
          <div className="hifz-progress-loading">Loading…</div>
        </div>
      </>
    );
  }

  return (
    <>
      <NavbarAll />
      <div className="hifz-progress-wrapper">
        <div className="hifz-progress-topbar" role="banner">
          <button
            type="button"
            className="hifz-progress-back-btn"
            onClick={goToDashboard}
            aria-label="Back to dashboard"
          >
            ← Dashboard
          </button>
          <div className="hifz-progress-topbar-meta">
            <div className="hifz-progress-topbar-titles">
              <h1 className="hifz-progress-title">Attendance</h1>
              <p className="hifz-progress-subtitle">
                {currentMonthName} {currentYear}
              </p>
            </div>
            <div className="hifz-progress-topbar-actions">
              <div className="hifz-progress-student-chip">
                <div className="hifz-progress-avatar" aria-hidden="true">
                  {getInitial(studentDisplayName)}
                </div>
                <span className="hifz-progress-student-name">{studentDisplayName}</span>
              </div>
              {student && (
                <button
                  type="button"
                  className="hifz-progress-profile-btn"
                  onClick={goToProfile}
                >
                  Profile
                </button>
              )}
            </div>
          </div>
        </div>

        <main className="hifz-progress-main">
          <section
            className="hifz-progress-stats hifz-attendance-stats"
            aria-label="Attendance summary"
          >
            <div className="hifz-progress-stat-card">
              <span className="hifz-progress-stat-value">
                {loading
                  ? "…"
                  : attendanceSummary
                    ? `${attendanceSummary.presentDays}/${attendanceSummary.totalDays}`
                    : "—"}
              </span>
              <span className="hifz-progress-stat-label">Days present</span>
            </div>
            <div className="hifz-progress-stat-card hifz-progress-stat-accent">
              <span className="hifz-progress-stat-value">
                {loading ? "…" : attendanceSummary?.attendancePercentage ?? "—"}
              </span>
              <span className="hifz-progress-stat-label">Attendance</span>
            </div>
          </section>

          <section className="hifz-progress-calendar-card hifz-attendance-month-card">
            <div className="hifz-progress-month-nav">
              {showPrevButton ? (
                <button
                  type="button"
                  className="hifz-progress-month-btn"
                  onClick={handlePrevMonth}
                >
                  ◀ Prev
                </button>
              ) : (
                <span />
              )}
              <h2 className="hifz-progress-month-title">
                {currentMonthName} {currentYear}
              </h2>
              {showNextButton ? (
                <button
                  type="button"
                  className="hifz-progress-month-btn"
                  onClick={handleNextMonth}
                >
                  Next ▶
                </button>
              ) : (
                <span />
              )}
            </div>

            {loading ? (
              <p className="hifz-attendance-message">Loading attendance…</p>
            ) : attendanceSummary ? (
              <div className="hifz-attendance-details">
                <div className="hifz-attendance-row">
                  <span className="hifz-attendance-row-label">Days present</span>
                  <span className="hifz-attendance-row-value">
                    {attendanceSummary.presentDays} of {attendanceSummary.totalDays} school days
                  </span>
                </div>
                <div className="hifz-attendance-row">
                  <span className="hifz-attendance-row-label">Attendance rate</span>
                  <span className="hifz-attendance-row-value hifz-attendance-rate">
                    {attendanceSummary.attendancePercentage}
                  </span>
                </div>
              </div>
            ) : (
              <p className="hifz-attendance-message">No attendance data for this month.</p>
            )}
          </section>

          <footer className="hifz-progress-footer">
            Developed by{" "}
            <a href="https://codevente.com" target="_blank" rel="noopener noreferrer">
              Codevente
            </a>
          </footer>
        </main>
      </div>
    </>
  );
}
